/* eslint-disable no-undef */
// NOTE: Adapted from: https://github.com/umdjs/umd/blob/master/templates/returnExports.js
(function (root, factory) {
    'use strict';
    if (typeof define === 'function' && define.amd) {
        // AMD
        define([], function () {
            return factory();
        });
    } else if (typeof module === 'object' && module.exports) {
        // CommonJS
        module.exports = factory();
    } else {
        // Browser globals
        root.TikTokPixel = factory();
    }
    // eslint-disable-next-line no-invalid-this
})(this, function () {
    'use strict';

    /**
     * This class allows developers to track events to one or more TikTok pixels
     * The main methods within this class are:
     *
     * init
     *      Init's arguments are:
     *          `trackingDataArray` - array of objects
     *             - Each object contains a `pixelId` and optional `events` configuration
     *             - The `pixelId` can be obtained by setting up a TikTok Pixel
     *      The init method initializes all TikTok pixel codes on the page using the instance API
     *      so that you can then track events across multiple pixels.
     *      Reference: https://business-api.tiktok.com/portal/docs?id=1739585702922241
     *
     * track
     *      Track's arguments are:
     *          `eventName` - string
     *          `data` (optional) - null or object
     *              - The `eventName` is the event that you want to send to TikTok
     *              - The `data` argument is data that you can pass to the event. This is optional since not every event
     *              requires additional data.
     *      The track method allows you to send information to TikTok about actions the user takes
     *      (e.g. viewing a page, making a purchase, etc). Events are tracked across all initialized pixels.
     *      The Pixel needs to be initialized on the page, by calling init(), before you can successfully use this method.
     */

    const TikTokPixel = class {
        constructor(debug = false, trackingData = null) {
            this.initialized = !!window.ttq;
            this.debug = debug;
            this.trackingData = trackingData;
        }

        _error(message) {
            console.error(`${message}`);
        }

        _log(message) {
            console.log(`${message}`);
        }

        _verifyInit() {
            // Check all required conditions for pixel to be properly initialized
            const hasWindowTtq = !!(
                typeof window !== 'undefined' && window.ttq
            );
            const hasTtqInstance = !!this._ttq;
            const hasTrackingData = !!(
                this.trackingData &&
                Array.isArray(this.trackingData) &&
                this.trackingData.length > 0
            );
            const isInitialized = this.initialized;

            const isFullyInitialized =
                hasWindowTtq &&
                hasTtqInstance &&
                hasTrackingData &&
                isInitialized;

            if (!isFullyInitialized && this.debug) {
                this._log(
                    `TIKTOK: Pixel not fully initialized - ` +
                        `window.ttq: ${hasWindowTtq}, ` +
                        `_ttq instance: ${hasTtqInstance}, ` +
                        `trackingData: ${hasTrackingData}, ` +
                        `initialized flag: ${isInitialized}`,
                );
            }

            return isFullyInitialized;
        }

        _pixelCode() {
            // This is code from TikTok - do not modify
            !(function (w, d, t) {
                // Initialize TikTok Analytics object
                w.TiktokAnalyticsObject = t;
                var ttq = (w[t] = w[t] || []);

                // Define available methods
                ttq.methods = [
                    'page',
                    'track',
                    'identify',
                    'instances',
                    'debug',
                    'on',
                    'off',
                    'once',
                    'ready',
                    'alias',
                    'group',
                    'enableCookie',
                    'disableCookie',
                    'holdConsent',
                    'revokeConsent',
                    'grantConsent',
                ];

                // Helper function to set and defer method calls
                ttq.setAndDefer = function (t, e) {
                    t[e] = function () {
                        t.push(
                            [e].concat(
                                Array.prototype.slice.call(arguments, 0),
                            ),
                        );
                    };
                };

                // Initialize all methods
                for (var i = 0; i < ttq.methods.length; i++) {
                    ttq.setAndDefer(ttq, ttq.methods[i]);
                }

                // Instance management
                ttq.instance = function (t) {
                    for (
                        var e = ttq._i[t] || [], n = 0;
                        n < ttq.methods.length;
                        n++
                    ) {
                        ttq.setAndDefer(e, ttq.methods[n]);
                    }
                    return e;
                };

                // Load the TikTok pixel
                ttq.load = function (e, n) {
                    var r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
                    var o = n && n.partner;

                    ttq._i = ttq._i || {};
                    ttq._i[e] = [];
                    ttq._i[e]._u = r;
                    ttq._t = ttq._t || {};
                    ttq._t[e] = +new Date();
                    ttq._o = ttq._o || {};
                    ttq._o[e] = n || {};

                    // Create and append script
                    n = document.createElement('script');
                    n.type = 'text/javascript';
                    n.async = !0;
                    n.src = r + '?sdkid=' + e + '&lib=' + t;
                    e = document.getElementsByTagName('script')[0];
                    e.parentNode.insertBefore(n, e);
                };
            })(window, document, 'ttq');
        }

        init(trackingDataArray) {
            if (
                !trackingDataArray ||
                !Array.isArray(trackingDataArray) ||
                trackingDataArray.length === 0
            ) {
                if (this.debug) {
                    this._error(
                        'Need to insert tracking data array for initializing',
                    );
                }
                return;
            }
            this._pixelCode();
            this.initialized = true;

            this._ttq = window.ttq;
            if (this.debug) {
                this._log(
                    `TIKTOK: Tracking Data ${JSON.stringify(
                        this.trackingData,
                    )}`,
                );
            }

            // Deduplicate pixels by pixelId to prevent double-firing of events
            const seenPixelIds = new Set();
            const deduplicatedTrackingData = trackingDataArray.filter(
                (pixelData) => {
                    const pixelId = pixelData.pixelId;
                    if (!pixelId) return false;

                    if (seenPixelIds.has(pixelId)) {
                        if (this.debug) {
                            this._log(
                                `TIKTOK: Skipping duplicate pixel ID: ${pixelId}`,
                            );
                        }
                        return false;
                    }

                    seenPixelIds.add(pixelId);
                    return true;
                },
            );

            // Store deduplicated tracking data for use in track() and page() methods
            this.trackingData = deduplicatedTrackingData;

            // Initialize each pixel using ttq.load()
            deduplicatedTrackingData.forEach((pixelData) => {
                const pixelId = pixelData.pixelId;
                if (this.debug) {
                    this._log(`TIKTOK: Initialized pixel ID: ${pixelId}`);
                }
                this._ttq.load(pixelId);
            });

            // Call page() once after all pixels are loaded
            this._ttq.page();
        }

        track(eventName, data = null) {
            if (!this._verifyInit()) return;

            if (!this.trackingData || !Array.isArray(this.trackingData)) return;

            if (!this._ttq) return;

            // Track events for all initialized pixels using instance API
            this.trackingData.forEach((pixelData) => {
                const pixelId = pixelData.pixelId;
                if (!pixelId) return;

                const trackingEvents = pixelData.events;

                // Event to fire based on trigger placement
                // e.g. 'ViewContent' for 'event_listing'
                let event = trackingEvents ? trackingEvents[eventName] : null;

                let deduplication;
                // Always track purchase event regardless of tracking config
                if (eventName === 'Completed Order') {
                    event = 'Purchase';
                    // Deduplication for Pixel SDK & Events API
                    // https://business-api.tiktok.com/portal/docs?id=1739584864945154
                    deduplication = { event_id: data.orderId };
                }

                if (!event) {
                    if (this.debug) {
                        this._log(
                            `TIKTOK: No event to fire for ${eventName} on pixel ${pixelId}`,
                        );
                    }
                    return;
                }

                const payload = {};
                if (data) {
                    // Present for event_listing or pick_a_seat
                    if (data.eventId) {
                        payload.content_id = data.eventId;
                        payload.content_type = 'product_group';
                    }

                    // Transform payload for order tracking
                    if (data.products) {
                        payload.contents = data.products.map((product) => {
                            return {
                                content_id: product.id || product.sku,
                                content_name: product.name,
                                content_type: 'product_group',
                                quantity: Number(product.quantity),
                                price: Number(product.price),
                            };
                        });
                        payload.content_type = 'product';
                        payload.currency = data.currency;
                        payload.value = Number(data.revenue);
                    }
                }

                if (this.debug) {
                    this._log(
                        `TIKTOK: Tracking event: ${event} on pixel ${pixelId} with payload: ${JSON.stringify(
                            payload,
                        )}`,
                    );
                }

                // Use instance API to track event for this specific pixel
                if (deduplication) {
                    this._ttq
                        .instance(pixelId)
                        .track(event, payload, deduplication);
                } else {
                    this._ttq.instance(pixelId).track(event, payload);
                }
            });
        }

        page(pageName) {
            if (!this._verifyInit()) return;

            if (!this.trackingData || !Array.isArray(this.trackingData)) return;

            if (!this._ttq) return;

            // Track page view for all initialized pixels using instance API
            this.trackingData.forEach((pixelData) => {
                const pixelId = pixelData.pixelId;
                if (pixelId) {
                    this._ttq.instance(pixelId).page({ description: pageName });
                }
            });
        }

        identify(uuid, data = null) {
            if (!this._verifyInit()) return;

            if (!this._ttq) return;

            const params = [];

            if (data) {
                params.push(data);
            }

            this._ttq.identify(...params);
        }
    };

    return TikTokPixel;
});
